<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { CategoryType, FilterEventStatus } from "mws-ts-v2-sdk";

import useCategorySorting from "~/composables/useCategorySorting";
import { queries } from "~/queries";
import { CategorySorting, CategoryTabs } from "~/types/Category";

const { categorySlug, activeFilters, activeTab } = defineProps<{
  activeFilters: string[];
  activeTab: CategoryTabs;
  categorySlug: string;
}>();

const emit = defineEmits<{
  (e: "close-menu"): void;
}>();

const route = useRoute();
const router = useRouter();
const statusMap = {
  [CategoryTabs.Upcoming]: FilterEventStatus.Upcoming,
  [CategoryTabs.Historical]: FilterEventStatus.Finished,
  [CategoryTabs.Live]: FilterEventStatus.Live,
} as const;

const eventStatusFilter = computed(() => {
  return statusMap[activeTab];
});

const { data: filterCategories, isLoading: isFilterCategoriesLoading } =
  useQuery({
    ...queries.categories.filterCategories(
      eventStatusFilter,
      () => categorySlug,
    ),
    staleTime: Infinity,
  });

const { defaultValue: eventSortingDefaultValue, options: eventSortingOptions } =
  useCategorySorting(() => activeTab);

const categoryFilterOptions = computed(() => {
  if (!filterCategories.value) return [];

  const optionsByType: {
    categoryType: CategoryType;
    options: { label: string; value: string }[];
  }[] = [];

  Object.values(CategoryType).forEach((categoryType) => {
    const options = filterCategories.value
      .filter((c) => c.externalReference?.type === categoryType)
      .map((c) => ({
        label: c.name,
        value: c.id,
      }));

    if (options.length > 0) {
      optionsByType.push({ categoryType, options });
    }
  });

  return optionsByType;
});

const { filtersApplied, filtersCleared } = useAnalytics();

const onCloseFilterMenu = () => {
  filtersApplied({
    sort:
      transformRouteParams(route.query.sort, "string") ??
      CategorySorting.Featured,
    filters: getAppliedFilterTypesCategoryPage(
      activeFilters,
      categoryFilterOptions.value,
    ),
    page_type: "categoryPage",
  });

  emit("close-menu");
};

const onFiltersCleared = () => {
  router.replace({
    ...route,
    query: {
      tab: route.query.tab,
    },
  });

  filtersCleared({ page_type: "categoryPage" });
  onCloseFilterMenu();
};
</script>

<template>
  <BaseMenuWithHeader
    :title="$t('filter.title')"
    :hide-back-button="true"
    @close-menu="onCloseFilterMenu"
  >
    <div class="category-event-filters-content">
      <BaseSkeletonLoader
        v-if="isFilterCategoriesLoading"
        :height="40"
        :width="200"
        class="mb-4"
      />
      <BaseFilterSection
        unique-name="sort"
        type="single"
        :label="$t('sort.title')"
        :default-value="eventSortingDefaultValue"
        :options="eventSortingOptions"
      />
      <BaseFilterSection
        v-for="categoryOptions in categoryFilterOptions"
        :key="categoryOptions.categoryType"
        type="multiple"
        unique-name="category"
        :label="$t(`category.types.${categoryOptions.categoryType}`)"
        :options="categoryOptions.options"
      />
    </div>
    <template #actions>
      <BaseFilterControlButtons
        @clear-filters="onFiltersCleared"
        @apply-filters="onCloseFilterMenu"
      />
    </template>
  </BaseMenuWithHeader>
</template>

<style lang="postcss" scoped>
.category-event-filters-content {
  @apply normal-case;
}
</style>
